import { useState, useMemo } from "react";
import { Search, Users } from "lucide-react";
import { formatLettresDate } from "../../utils/ClasseEvaluations";
import MathText from '../VueQuestion/MathText';
import { PastillesNiveaux } from '../CollerNotes/PastilleNiveau';

// ─── Palette (identique AcquisitionEleve) ───
const T = {
  navy: "#1E3A8A", blueMid: "#3B5FCC", blueLight: "#5B7FE6", blueAccent: "#4A90D9",
  textPrimary: "#1A2744", textSecondary: "#6B7A99", textMuted: "#9AA5BD",
  borderLight: "rgba(30,58,138,0.08)", borderMid: "rgba(30,58,138,0.12)",
  green: "#22C55E", greenBg: "rgba(34,197,94,0.07)", greenBorder: "rgba(34,197,94,0.15)",
  orange: "#F59E0B", orangeBg: "rgba(245,158,11,0.07)", orangeBorder: "rgba(245,158,11,0.15)",
  red: "#EF4444", redBg: "rgba(239,68,68,0.06)", redBorder: "rgba(239,68,68,0.12)",
};

// ─── Helpers statut ───
const getStatus = (m) => {
  if (m >= 5) return "acquis";
  if (m > 3) return "encours";
  if (m >= 1) return "fragile";
  return "neval";
};

const getConf = (s) => ({
  acquis:  { label: "Acquis",      color: T.green,  bg: T.greenBg,  border: T.greenBorder },
  encours: { label: "En cours",    color: T.orange, bg: T.orangeBg, border: T.orangeBorder },
  fragile: { label: "Non acquis",  color: T.red,    bg: T.redBg,    border: T.redBorder },
  neval:   { label: "Non évalué",  color: "#9CA3AF", bg: "rgba(156,163,175,0.07)", border: "rgba(156,163,175,0.15)" },
}[s]);

// ─── Grain texture SVG ───
const GRAIN = "url(\"data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='150' height='150'%3E%3Cfilter id='n'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='.9' numOctaves='3' stitchTiles='stitch'/%3E%3C/filter%3E%3Crect width='150' height='150' filter='url(%23n)' opacity='.5'/%3E%3C/svg%3E\")";

// ─── CSS responsive (même structure que AcquisitionEleve) ───
const CARDS_CSS = `
  .sav-root {
    --card-pad: 20px 22px; --card-r: 18px;
    --score-fs: 30px; --score-sub: 14px;
    --title-fs: 14px; --meta-fs: 11px;
    --grid-min: 330px; --grid-gap: 14px;
    --sum-pad: 22px 24px; --sum-title: 15px; --sum-label: 13px; --sum-item: 12px; --sum-gap: 20px; --sum-mb: 18px;
    --search-pad: 12px 16px; --search-fs: 12px;
    --pill-fs: 11px; --pill-pad: 4px 10px;
    --legend-fs: 10px; --legend-dot: 8px;
    --filter-icon: 10px; --filter-gap: 5px;
    --title-clamp: 3; --sum-clamp: 1;
    --bar-h: 6px;
    padding: var(--sav-pad, 20px 0);
  }

  /* ─ Mobile ─ */
  @media (max-width:639px) {
    .sav-root {
      --card-pad: 16px 18px; --card-r: 14px;
      --score-fs: 24px; --score-sub: 11px;
      --title-fs: 13px; --meta-fs: 10px;
      --grid-min: 280px; --grid-gap: 10px;
      --sum-pad: 16px 18px; --sum-title: 13px; --sum-label: 11px; --sum-item: 11px; --sum-gap: 12px; --sum-mb: 14px;
      --search-pad: 10px 14px; --search-fs: 11px;
      --pill-fs: 10px; --pill-pad: 3px 8px;
      --title-clamp: 2; --sum-clamp: 2;
      --bar-h: 5px;
    }
    .sav-sum-cols { grid-template-columns: 1fr !important }
  }

  /* ─ xl: 1280-1699px — compact ─ */
  @media (min-width:1280px) and (max-width:1699px) {
    .sav-root {
      --card-pad: 14px 16px; --card-r: 14px;
      --score-fs: 22px; --score-sub: 11px;
      --title-fs: 12px; --meta-fs: 10px;
      --grid-min: 250px; --grid-gap: 10px;
      --sum-pad: 14px 16px; --sum-title: 13px; --sum-label: 11px; --sum-item: 10px; --sum-gap: 14px; --sum-mb: 12px;
      --search-pad: 8px 12px; --search-fs: 11px;
      --pill-fs: 10px; --pill-pad: 3px 7px;
      --legend-fs: 9px; --legend-dot: 7px;
      --filter-icon: 9px; --filter-gap: 4px;
      --title-clamp: 2; --sum-clamp: 2;
      --bar-h: 5px;
    }
    .sav-sum-cols { grid-template-columns: 1fr 1fr !important }
  }

  /* ─ 2xl: 1700-1919px — medium ─ */
  @media (min-width:1700px) and (max-width:1919px) {
    .sav-root {
      --card-pad: 16px 18px; --card-r: 16px;
      --score-fs: 26px; --score-sub: 12px;
      --title-fs: 13px; --meta-fs: 10px;
      --grid-min: 290px; --grid-gap: 12px;
      --sum-pad: 18px 20px; --sum-title: 14px; --sum-label: 12px; --sum-item: 11px; --sum-gap: 16px; --sum-mb: 14px;
      --search-pad: 10px 14px; --search-fs: 11px;
      --pill-fs: 10px; --pill-pad: 3px 9px;
      --legend-fs: 9px; --legend-dot: 7px;
      --filter-icon: 9px;
      --title-clamp: 2; --sum-clamp: 2;
      --bar-h: 5px;
    }
  }

  /* 3xl: 1920px+ → defaults */

  /* ─ Card title truncation ─ */
  .sav-card-title {
    display: -webkit-box; -webkit-box-orient: vertical;
    -webkit-line-clamp: var(--title-clamp, 3);
    overflow: hidden; text-overflow: ellipsis;
    word-break: break-word;
  }

  /* ─ Summary item text ─ */
  .sav-sum-text {
    display: -webkit-box; -webkit-box-orient: vertical;
    -webkit-line-clamp: var(--sum-clamp, 1);
    overflow: hidden; text-overflow: ellipsis;
    word-break: break-word; line-height: 1.35;
  }

  /* ─ Hover tooltip ─ */
  .sav-tip { position: relative; }
  .sav-tip .sav-tip-full {
    display: none; position: absolute; z-index: 9999;
    bottom: calc(100% + 6px); left: 0;
    padding: 8px 12px; max-width: 340px; width: max-content;
    background: #1E293B; color: #F1F5F9; font-size: 12px; line-height: 1.45;
    font-weight: 500; border-radius: 10px;
    box-shadow: 0 12px 32px rgba(0,0,0,0.18);
    pointer-events: none; white-space: normal; word-break: break-word;
  }
  .sav-tip .sav-tip-full::after {
    content: ''; position: absolute; top: 100%; left: 16px;
    border: 5px solid transparent; border-top-color: #1E293B;
  }
  .sav-tip:hover .sav-tip-full { display: block; }

  .sav-card{transition:all .3s cubic-bezier(.4,0,.2,1);cursor:pointer;position:relative;z-index:1}
  .sav-card:hover{transform:translateY(-5px)!important;z-index:10!important;box-shadow:0 20px 56px rgba(30,58,138,0.14), 0 8px 20px rgba(30,58,138,0.08), 0 2px 6px rgba(30,58,138,0.05), inset 0 1px 0 rgba(255,255,255,0.95)!important}
`;

// ─── Card style (glassmorphism) ───
const CARD_STYLE = {
  background: "linear-gradient(180deg, rgba(255,255,255,0.88) 0%, rgba(255,255,255,0.86) 70%, rgba(235,243,255,0.78) 100%)",
  backdropFilter: "blur(8px)", WebkitBackdropFilter: "blur(8px)",
  padding: "var(--card-pad)", borderRadius: "var(--card-r)",
  border: "1px solid rgba(255,255,255,0.65)",
  boxShadow: "0 12px 40px rgba(30,58,138,0.1), 0 4px 12px rgba(30,58,138,0.06), 0 1px 3px rgba(30,58,138,0.04), inset 0 1px 0 rgba(255,255,255,0.9)",
  position: "relative", overflow: "visible", cursor: "pointer",
  display: "flex", flexDirection: "column",
};

// ─── Score (monospace) ───
const Score = ({ score, max = 6, sz = "md" }) => {
  const c = getConf(getStatus(score));
  const fsVar = sz === "sm" ? 14 : "var(--score-fs)";
  const subVar = sz === "sm" ? "0.5em" : "var(--score-sub)";
  return (
    <span style={{ fontFamily: "monospace", fontWeight: 700, color: c.color, fontSize: fsVar, lineHeight: 1 }}>
      {score.toFixed(1)}
      <span style={{ fontSize: subVar, color: T.textMuted, fontWeight: 500 }}>/{max}</span>
    </span>
  );
};

// ─── Pill ───
const Pill = ({ status, small }) => {
  const c = getConf(status);
  return (
    <span style={{
      padding: small ? "2px 8px" : "var(--pill-pad)", borderRadius: 20,
      fontSize: small ? 10 : "var(--pill-fs)", fontWeight: 700,
      background: c.bg, color: c.color, border: `1px solid ${c.border}`,
      whiteSpace: "nowrap", flexShrink: 0,
    }}>
      {c.label}
    </span>
  );
};

// ─── Section Résumé ───
const SummarySection = ({ items, viewLevel, niveauClasse }) => {
  const forces = items.filter(i => getStatus(i.moyenneClasse) === "acquis");
  const fragiles = items.filter(i => getStatus(i.moyenneClasse) === "fragile");
  const enCours = items.filter(i => getStatus(i.moyenneClasse) === "encours");

  const fLbl = viewLevel === 'theme' ? "Thèmes forts" : viewLevel === 'descripteur' ? "Notions fortes" : "Savoirs forts";
  const frLbl = viewLevel === 'theme' ? "Thèmes fragiles" : viewLevel === 'descripteur' ? "Notions fragiles" : "Savoirs fragiles";

  const cols = [
    { items: forces, label: fLbl, icon: "✅" },
    { items: fragiles, label: frLbl, icon: "🔴" },
    { items: enCours, label: "En cours", icon: "🟡" },
  ];

  return (
    <div style={{
      background: "linear-gradient(180deg, rgba(255,255,255,0.88) 0%, rgba(255,255,255,0.86) 70%, rgba(235,243,255,0.78) 100%)",
      backdropFilter: "blur(8px)", WebkitBackdropFilter: "blur(8px)",
      borderRadius: "var(--card-r)", padding: "var(--sum-pad)",
      border: "1px solid rgba(255,255,255,0.7)",
      boxShadow: "0 12px 40px rgba(30,58,138,0.09), 0 4px 12px rgba(30,58,138,0.05), inset 0 1px 0 rgba(255,255,255,0.9)",
      marginBottom: "var(--sum-mb)", position: "relative", overflow: "hidden"
    }}>
      {/* Grain */}
      <div style={{ position: "absolute", inset: 0, opacity: 0.015, pointerEvents: "none", borderRadius: "var(--card-r)", backgroundImage: GRAIN, mixBlendMode: "multiply" }} />

   

      <div className="sav-sum-cols" style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: "var(--sum-gap)" }}>
        {cols.map((col, ci) => (
          <div key={ci}>
            <div style={{ display: "flex", alignItems: "center", gap: 6, marginBottom: 10 }}>
              <span style={{ fontSize: "var(--sum-label)" }}>{col.icon}</span>
              <span style={{ fontSize: "var(--sum-label)", fontWeight: 700, color: T.textPrimary }}>{col.label} ({col.items.length})</span>
            </div>
            {col.items.length === 0 ? (
              <div style={{ fontSize: "var(--sum-item)", color: T.textMuted, fontStyle: "italic" }}>Aucun</div>
            ) : (
              col.items.slice(0, 4).map((d, i) => {
                const titre = d.nom || d.savoirNom;
                return (
                  <div key={i} className={titre?.length > 30 ? "sav-tip" : ""} style={{ fontSize: "var(--sum-item)", color: T.textSecondary, marginBottom: 5, display: "flex", alignItems: "flex-start", gap: 6 }}>
                    <PastillesNiveaux niveaux={d.niveaux} niveauClasse={niveauClasse} />
                    <span className="sav-sum-text" style={{ flex: 1, minWidth: 0, cursor: titre?.length > 30 ? "help" : undefined }}>
                      <MathText>{titre}</MathText>
                    </span>
                    <Score score={d.moyenneClasse} max={6} sz="sm" />
                    {titre?.length > 30 && <div className="sav-tip-full"><MathText>{titre}</MathText></div>}
                  </div>
                );
              })
            )}
          </div>
        ))}
      </div>
    </div>
  );
};

// ─── Filtres acquisition ───
const STATUS_FILTERS = [
  { key: "all",     label: "Tous",        icon: "📊" },
  { key: "acquis",  label: "Acquis",      icon: "✅", color: T.green,  bg: T.greenBg,  border: T.greenBorder },
  { key: "encours", label: "En cours",    icon: "🟡", color: T.orange, bg: T.orangeBg, border: T.orangeBorder },
  { key: "fragile", label: "Non acquis",  icon: "🔴", color: T.red,    bg: T.redBg,    border: T.redBorder },
];

// ─── Card ───
const SavoirCard = ({ item, viewLevel, index, loaded, onClick, niveauClasse }) => {
  const moy = item.moyenneClasse;
  const st = getStatus(moy);
  const conf = getConf(st);
  const titre = item.nom || item.savoirNom;
  const nbEvals = item.evalDates?.size || 0;

  return (
    <div
      className="sav-card"
      onClick={() => onClick(item)}
      style={{
        ...CARD_STYLE,
        opacity: loaded ? 1 : 0,
        transform: loaded ? "translateY(0)" : "translateY(12px)",
        transition: `all 0.4s cubic-bezier(.4,0,.2,1) ${index * 35}ms`,
      }}
    >
      {/* Grain */}
      <div style={{
        position: "absolute", inset: 0, opacity: 0.018, pointerEvents: "none",
        borderRadius: "var(--card-r)", overflow: "hidden",
        backgroundImage: GRAIN, mixBlendMode: "multiply",
        background: "linear-gradient(135deg, rgba(30,58,138,0.03), rgba(59,95,204,0.02))"
      }} />

      <div style={{ position: "relative", zIndex: 1, display: "flex", flexDirection: "column", flex: 1 }}>
        {/* Header : titre + pill */}
        <div style={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", marginBottom: 8, gap: 10 }}>
          {/* « 6e » : attendus d'un autre niveau que la classe (2026-09-29). */}
          <PastillesNiveaux niveaux={item.niveaux} niveauClasse={niveauClasse} />
          <div className={titre?.length > 40 ? "sav-tip" : ""} style={{ flex: 1, minWidth: 0 }}>
            <h3 className="sav-card-title" style={{ margin: 0, fontSize: "var(--title-fs)", fontWeight: 700, lineHeight: 1.35, color: T.textPrimary }}>
              <MathText>{titre}</MathText>
            </h3>
            {titre?.length > 40 && <div className="sav-tip-full"><MathText>{titre}</MathText></div>}
          </div>
          <Pill status={st} small />
        </div>

        {/* Label thème (pour descripteur) */}
        {viewLevel === 'descripteur' && item.themeNom && (
          <div className={item.themeNom.length > 35 ? "sav-tip" : ""} style={{ fontSize: "var(--meta-fs)", color: T.textMuted, marginBottom: 6 }}>
            <span style={{ display: "block", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", cursor: item.themeNom.length > 35 ? "help" : undefined }}>
              {item.themeNom}
            </span>
            {item.themeNom.length > 35 && <div className="sav-tip-full">{item.themeNom}</div>}
          </div>
        )}

        {/* Score */}
        <div style={{ display: "flex", alignItems: "baseline", gap: 12, marginBottom: 6 }}>
          <div style={{ fontSize: "var(--score-fs)", fontWeight: 800, fontFamily: "monospace", lineHeight: 1, color: conf.color }}>
            {moy.toFixed(1)}<span style={{ fontSize: "var(--score-sub)", fontWeight: 500, color: T.textMuted }}>/6</span>
          </div>
          <div style={{ fontSize: "var(--meta-fs)", color: T.textSecondary }}>
            moy. classe
          </div>
        </div>

        {/* Info counts */}
        <div style={{ fontSize: "var(--meta-fs)", color: T.textMuted, marginBottom: 10 }}>
          <span style={{ display: "flex", alignItems: "center", gap: 4 }}>
            <Users size={13} style={{ color: T.textMuted, flexShrink: 0 }} />
            <strong style={{ color: T.textPrimary }}>{item.nbEleves}</strong> élèves
            {nbEvals > 0 && <> · 📝 {nbEvals} éval{nbEvals > 1 ? 's' : ''}</>}
          </span>
        </div>

        {/* Barre de répartition — poussée en bas */}
        <div style={{ marginTop: "auto", paddingTop: 10, borderTop: "2px solid rgba(30,58,138,0.1)", position: "relative" }}>
          <div style={{ position: "absolute", top: -1, left: 0, right: 0, height: 1, background: "linear-gradient(90deg, transparent 0%, rgba(30,58,138,0.06) 20%, rgba(30,58,138,0.06) 80%, transparent 100%)" }} />

          {/* Bar */}
          <div style={{ display: "flex", gap: 3, height: "var(--bar-h)", borderRadius: 3, overflow: "hidden", background: "rgba(30,58,138,0.05)", marginBottom: 6 }}>
            {item.nbEvalSatisfaisant > 0 && <div style={{ flex: item.nbEvalSatisfaisant, background: T.green, borderRadius: 3 }} />}
            {item.nbEvalEnCours > 0 && <div style={{ flex: item.nbEvalEnCours, background: T.orange, borderRadius: 3 }} />}
            {item.nbEvalFragile > 0 && <div style={{ flex: item.nbEvalFragile, background: T.red, borderRadius: 3 }} />}
          </div>

          {/* Distribution dots */}
          <div style={{ display: "flex", gap: 10, fontSize: "var(--legend-fs)", color: T.textMuted }}>
            {item.nbEvalSatisfaisant > 0 && (
              <span style={{ display: "flex", alignItems: "center", gap: 3 }}>
                <div style={{ width: "var(--legend-dot)", height: "var(--legend-dot)", borderRadius: "50%", background: T.green }} />
                {item.nbEvalSatisfaisant}
              </span>
            )}
            {item.nbEvalEnCours > 0 && (
              <span style={{ display: "flex", alignItems: "center", gap: 3 }}>
                <div style={{ width: "var(--legend-dot)", height: "var(--legend-dot)", borderRadius: "50%", background: T.orange }} />
                {item.nbEvalEnCours}
              </span>
            )}
            {item.nbEvalFragile > 0 && (
              <span style={{ display: "flex", alignItems: "center", gap: 3 }}>
                <div style={{ width: "var(--legend-dot)", height: "var(--legend-dot)", borderRadius: "50%", background: T.red }} />
                {item.nbEvalFragile}
              </span>
            )}
          </div>

          {/* Date */}
          {item.dernierEval && (
            <div style={{ fontSize: "var(--legend-fs)", color: T.textMuted, marginTop: 4 }}>
              📅 {formatLettresDate(item.dernierEval)}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

// ─── Composant principal ───
const AcquisitionSavoirsCards = ({ items, viewLevel, onCardClick, searchTerm: externalSearch, setSearchTerm: externalSetSearch, niveauClasse = '' }) => {
  const [internalSearch, setInternalSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [loaded, setLoaded] = useState(false);

  const search = externalSearch !== undefined ? externalSearch : internalSearch;
  const setSearch = externalSetSearch || setInternalSearch;

  // Trigger load animation
  useState(() => { setTimeout(() => setLoaded(true), 80); });

  const filtered = useMemo(() => {
    if (!items) return [];
    let list = items;

    if (search) {
      const term = search.toLowerCase();
      list = list.filter(i =>
        (i.nom || i.savoirNom || '').toLowerCase().includes(term) ||
        (i.themeNom || '').toLowerCase().includes(term)
      );
    }

    if (statusFilter !== 'all') {
      list = list.filter(i => getStatus(i.moyenneClasse) === statusFilter);
    }

    return list;
  }, [items, search, statusFilter]);

  const validItems = useMemo(() => {
    return items?.filter(i => i.moyenneClasse != null && i.moyenneClasse > 0) || [];
  }, [items]);

  return (
    <div className="sav-root" style={{ padding: "20px 0", minHeight: 200, position: "relative" }}>
      <style>{CARDS_CSS}</style>

      {validItems.length === 0 ? (
        <div style={{ textAlign: "center", padding: "60px 20px", color: T.textMuted }}>
          <div style={{ fontSize: 40, marginBottom: 12, opacity: 0.5 }}>∅</div>
          <div style={{ fontSize: "var(--title-fs)", fontWeight: 500 }}>Aucun résultat</div>
        </div>
      ) : (
        <>
          {/* Résumé */}
          <SummarySection items={validItems} viewLevel={viewLevel} niveauClasse={niveauClasse} />

          {/* Recherche & Filtres */}
          <div style={{
            background: "linear-gradient(180deg, rgba(255,255,255,0.88) 0%, rgba(255,255,255,0.86) 70%, rgba(235,243,255,0.78) 100%)",
            backdropFilter: "blur(8px)", WebkitBackdropFilter: "blur(8px)",
            borderRadius: "var(--card-r)", padding: "var(--search-pad)",
            border: "1px solid rgba(255,255,255,0.7)",
            boxShadow: "0 8px 28px rgba(30,58,138,0.07), 0 2px 8px rgba(30,58,138,0.04), inset 0 1px 0 rgba(255,255,255,0.9)",
            marginBottom: "var(--sum-mb)", display: "flex", alignItems: "center", gap: 10, flexWrap: "wrap"
          }}>
            {/* Input recherche */}
            <div style={{ position: "relative", flex: 1, minWidth: 140 }}>
              <Search size={14} style={{ position: "absolute", left: 9, top: "50%", transform: "translateY(-50%)", color: T.textMuted }} />
              <input
                type="text"
                placeholder={`Rechercher ${viewLevel === 'theme' ? 'un thème' : viewLevel === 'descripteur' ? 'une notion' : 'un savoir'}...`}
                value={search}
                onChange={e => setSearch(e.target.value)}
                style={{
                  width: "100%", paddingLeft: 28, paddingRight: 10, paddingTop: 6, paddingBottom: 6,
                  fontSize: "var(--search-fs)", background: "#FAFBFF",
                  border: `1.5px solid ${T.borderMid}`, borderRadius: 8, outline: "none",
                  color: T.textPrimary, fontFamily: "inherit",
                }}
              />
            </div>

            {/* Pills filtre par statut */}
            <div style={{ display: "flex", gap: "var(--filter-gap)", flexWrap: "wrap" }}>
              {STATUS_FILTERS.map(f => {
                const active = statusFilter === f.key;
                return (
                  <button
                    key={f.key}
                    onClick={() => setStatusFilter(f.key)}
                    style={{
                      padding: "var(--pill-pad)", borderRadius: 20,
                      fontSize: "var(--pill-fs)", fontWeight: 600, cursor: "pointer",
                      transition: "all 0.2s", display: "flex", alignItems: "center", gap: 4,
                      background: active ? (f.bg || T.blueAccent) : "rgba(30,58,138,0.03)",
                      color: active ? (f.color || "white") : T.textMuted,
                      border: active ? `1.5px solid ${f.border || T.blueAccent}` : `1.5px solid ${T.borderLight}`,
                    }}
                  >
                    <span style={{ fontSize: "var(--filter-icon)" }}>{f.icon}</span>
                    <span>{f.label}</span>
                    {f.key !== "all" && (
                      <span style={{ fontSize: "var(--filter-icon)", opacity: 0.7 }}>
                        ({validItems.filter(i => getStatus(i.moyenneClasse) === f.key).length})
                      </span>
                    )}
                  </button>
                );
              })}
            </div>

            {/* Légende répartition */}
            <div style={{ display: "flex", alignItems: "center", gap: 8, marginLeft: "auto" }}>
              <div style={{ display: "flex", alignItems: "center", gap: 3 }}>
                <div style={{ width: "var(--legend-dot)", height: "var(--legend-dot)", borderRadius: "50%", background: T.green }} />
                <span style={{ fontSize: "var(--legend-fs)", color: T.green, fontWeight: 600 }}>Acquis</span>
              </div>
              <div style={{ display: "flex", alignItems: "center", gap: 3 }}>
                <div style={{ width: "var(--legend-dot)", height: "var(--legend-dot)", borderRadius: "50%", background: T.orange }} />
                <span style={{ fontSize: "var(--legend-fs)", color: T.orange, fontWeight: 600 }}>En cours</span>
              </div>
              <div style={{ display: "flex", alignItems: "center", gap: 3 }}>
                <div style={{ width: "var(--legend-dot)", height: "var(--legend-dot)", borderRadius: "50%", background: T.red }} />
                <span style={{ fontSize: "var(--legend-fs)", color: T.red, fontWeight: 600 }}>Fragile</span>
              </div>
            </div>
          </div>

          {/* Grille de cards */}
          {filtered.length > 0 ? (
            <div style={{
              display: "grid",
              gridTemplateColumns: "repeat(auto-fill, minmax(var(--grid-min), 1fr))",
              gap: "var(--grid-gap)"
            }}>
              {filtered.map((item, i) => (
                <SavoirCard
                  key={item.uniqueKey || item.uniqueSavoirKey || i}
                  item={item}
                  viewLevel={viewLevel}
                  index={i}
                  loaded={loaded}
                  onClick={onCardClick}
                  niveauClasse={niveauClasse}
                />
              ))}
            </div>
          ) : (
            <div style={{ textAlign: "center", padding: "60px 20px", color: T.textMuted }}>
              <div style={{ fontSize: 40, marginBottom: 12, opacity: 0.5 }}>🔍</div>
              <div style={{ fontSize: "var(--title-fs)", fontWeight: 500 }}>Aucun résultat trouvé</div>
              <div style={{ fontSize: "var(--meta-fs)", marginTop: 6 }}>Essayez de modifier vos filtres</div>
            </div>
          )}
        </>
      )}
    </div>
  );
};

export default AcquisitionSavoirsCards;
